import { useState, useCallback, useRef } from 'react';
import deliveryChallanService from '../services/deliveryChallanService';
import i18n from '../i18n';

const useUnprintedChallans = () => {
    const [items, setItems] = useState([]);
    const [summary, setSummary] = useState(null);
    const [serverTime, setServerTime] = useState(null);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [marking, setMarking] = useState(null); // subOrderId being marked
    const [error, setError] = useState(null);

    // track when serverTime was received so client can tick locally
    const serverTimeReceivedAt = useRef(null);

    const fetchUnprinted = useCallback(async (params = {}) => {
        setLoading(true);
        setError(null);
        try {
            const res = await deliveryChallanService.getUnprinted(params);
            if (res.success) {
                const d = res.data;
                setItems(Array.isArray(d?.items) ? d.items : []);
                setSummary(d?.summary ?? null);
                setTotal(d?.total ?? 0);
                if (d?.server_time) {
                    setServerTime(d.server_time);
                    serverTimeReceivedAt.current = Date.now();
                }
            } else {
                setError(res.error);
            }
        } catch (err) {
            setError(err?.message || i18n.t('hookErrors.common.unexpected'));
        } finally {
            setLoading(false);
        }
    }, []);

    const markPrinted = useCallback(async (subOrderId) => {
        setMarking(subOrderId);
        try {
            const res = await deliveryChallanService.markChallanPrinted(subOrderId);
            if (res.success) {
                setItems(prev => prev.filter(i => i.sub_order_id !== subOrderId));
                setSummary(prev => prev ? { ...prev, total: Math.max(0, (prev.total ?? 1) - 1) } : prev);
            } else {
                // Was silently dropped — a challan already printed by someone else
                // (409) or since-deleted (404) left the row with no feedback.
                setError(res?.error || i18n.t('hookErrors.common.unexpected'));
            }
            return res;
        } catch (err) {
            setError(err?.message || i18n.t('hookErrors.common.unexpected'));
        } finally {
            setMarking(null);
        }
    }, []);

    return { items, summary, serverTime, total, loading, marking, error, fetchUnprinted, markPrinted };
};

export default useUnprintedChallans;
